// The settings switch (screen-mock `.tog`). 42×22 track, 14px knob, brand when
// on.
//
// **Not MUI's `Switch`.** That component carries a ripple, a hidden input, a
// thumb shadow and a size system, and every one of them would have to be
// overridden back out to reach a 42×22 pill. The geometry is bespoke and
// small, so it is written rather than undone.
//
// Controlled, and `onChange` is handed the new value rather than an event —
// every call site in the pause menu wants the boolean.

import { styled } from "@mui/material/styles";
import type { ComponentProps } from "react";

import { motion } from "./motion";

const Track = styled("button", {
  shouldForwardProp: (prop: string) => prop !== "on"
})<{ on?: boolean }>(({ theme, on }) => ({
  position: "relative",
  flex: "none",
  width: 42,
  height: 22,
  padding: 0,
  borderRadius: theme.kit.radius.pill,
  background: `color-mix(in srgb, ${theme.kit.color.panel} 20%, ${theme.kit.color.ground})`,
  border: `2px solid ${on ? theme.kit.color.brand : theme.kit.color.line}`,
  cursor: "pointer",
  ...motion([], ["border-color"]),
  "&:focus-visible": {
    outline: `2px solid ${theme.kit.color.brand}`,
    outlineOffset: 2
  },
  "&:disabled": { opacity: 0.38, pointerEvents: "none" },

  // The mobile foundation line. The switch may not grow — it sits in a 8px
  // settings row — so the *hit area* grows instead, 11px on every side of a
  // 42×22 track being exactly the 44px target.
  "@media (pointer: coarse)": {
    "&::after": { content: '""', position: "absolute", inset: -11 }
  }
}));

const Knob = styled("span", {
  shouldForwardProp: (prop: string) => prop !== "on"
})<{ on?: boolean }>(({ theme, on }) => ({
  position: "absolute",
  top: 1,
  left: on ? 20 : 2,
  width: 14,
  height: 14,
  borderRadius: "50%",
  background: on ? theme.kit.color.brand : theme.kit.color.inkDim,
  ...motion(["left"], ["background"])
}));

export interface ToggleProps
  extends Omit<ComponentProps<"button">, "onChange" | "type"> {
  checked: boolean;
  onChange?: (checked: boolean) => void;
}

export function Toggle({ checked, onChange, onClick, ...rest }: ToggleProps) {
  return (
    <Track
      type="button"
      role="switch"
      aria-checked={checked}
      on={checked}
      onClick={(event) => {
        onClick?.(event);
        onChange?.(!checked);
      }}
      {...rest}
    >
      <Knob on={checked} />
    </Track>
  );
}
